import { Injectable, OnDestroy, inject } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationError, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { VersionMismatchDialogComponent } from '../../components/version-mismatch-dialog/version-mismatch-dialog.component';

const CHUNK_ERROR_PATTERN = /Loading chunk .+ failed|Failed to fetch dynamically imported module/;

@Injectable({
  providedIn: 'root',
})
export class ChunkLoadErrorHandlerService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly dialog = inject(MatDialog);

  private routeEventSubscription: Subscription;
  private unhandledRejectionListener: (event: PromiseRejectionEvent) => void;

  activate(): void {
    this.routeEventSubscription?.unsubscribe();
    this.routeEventSubscription = this.router.events.pipe(
      filter(e => e instanceof NavigationError),
      tap((navigationError: NavigationError) => {
        if (navigationError?.error && CHUNK_ERROR_PATTERN.test(navigationError.error.message)) {
          this.handleChunkLoadError(navigationError.url);
        }
      }),
    ).subscribe();

    this.unhandledRejectionListener = (event: PromiseRejectionEvent) => {
      if (event.reason && CHUNK_ERROR_PATTERN.test(event.reason.message)) {
        this.handleChunkLoadError(this.location.path());
      }
    };

    window.addEventListener('unhandledrejection', this.unhandledRejectionListener);
  }

  private handleChunkLoadError(url: string): void {
    const isMismatchDialogOpen = this.dialog.openDialogs.some(
      dialog => dialog.componentInstance instanceof VersionMismatchDialogComponent,
    );

    if (isMismatchDialogOpen) {
      return;
    }
    const dialogRef = this.dialog.open(VersionMismatchDialogComponent, {
      disableClose: true,
      data: { url },
    });

    dialogRef.afterClosed().pipe(tap(
      result => {
        if (result) {
          this.location.replaceState(url);
          window.location.reload();
        }
      })).subscribe();
  }

  deactivate(): void {
    this.routeEventSubscription?.unsubscribe();
    if (this.unhandledRejectionListener) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionListener);
    }
  }

  ngOnDestroy(): void {
    this.deactivate();
  }
}
